import type { Identity } from "../types/identities-api.types";
import { isEligibleHolder } from "./holderEligibility";

/**
 * What the new-card modal learns about a holder it just created.
 *
 * Created is not the same as issuable. Every identity is born `PENDING_REVIEW` and its KYC starts `PENDING`,
 * while `isEligibleHolder` demands `ACTIVE` + `VERIFIED` — so `eligible` is the exception, not the rule. It
 * happens when the backend's global dedupe reused a person already verified elsewhere.
 */
export type NewHolderOutcome =
  /** Issuable right now: safe to select in the picker. */
  | { kind: "eligible"; identity: Identity }
  /** Created, but a card cannot be issued yet. `identity` is null when the read-back failed. */
  | { kind: "pending"; identityId: string; identity: Identity | null }
  /**
   * The POST succeeded but the response carried no id, so the holder exists and can be neither named nor
   * linked to. A distinct outcome rather than an absent one: the caller still has to say so, or the operator
   * creates the same person again.
   */
  | { kind: "unknown" };

/**
 * Decides what a creation produced, from the created id plus the identity read back with `GET
 * /internal/identities/:id`.
 *
 * Kept pure and separate from the drawer's effects for the same reason `reconcilePseDocumentType` and
 * `shouldHandOffToBank` are: the decisions get tested, the effects only get executed. This repo's `unit`
 * Vitest project runs in Node with no DOM, so a hook body cannot be rendered in a test.
 *
 * Three rules it encodes, each of which would be a real bug the other way round:
 *
 * - **A failed read-back is never `eligible`.** Not knowing is not the same as knowing it is fine; selecting a
 *   holder the backend will reject leaves the operator with a submit button that cannot work.
 * - **A read of a DIFFERENT id is discarded.** A racing or stale response must not vouch for this identity.
 * - **No id at all is `unknown`, never nothing.** The function is total on purpose: returning `null` let a
 *   caller drop the case on the floor, and the screen then repainted as if no holder had been created.
 */
export const resolveNewHolderOutcome = (
  createdIdentityId: string | undefined | null,
  readBack: Identity | null,
): NewHolderOutcome => {
  if (!createdIdentityId) return { kind: "unknown" };

  const confirmed = readBack && readBack.id === createdIdentityId ? readBack : null;

  if (confirmed && isEligibleHolder(confirmed)) {
    return { kind: "eligible", identity: confirmed };
  }
  return { kind: "pending", identityId: createdIdentityId, identity: confirmed };
};
